import { CloseIcon } from '@/components/icons/CloseIcon.tsx';
import { cn } from '@/utils/cn.ts';

type PanelWidthConfig = 'one-third' | 'one-fourth' | number;

const getFlexConfig = (panelWidth: PanelWidthConfig, isOpen: boolean) => {
	if (!isOpen) {
		return {
			main: 'grow-1 shrink-1 basis-0',
			panel: 'grow-0 shrink-0 basis-0',
		};
	}

	if (panelWidth === 'one-third') {
		return {
			main: 'grow-2 shrink-1 basis-0',
			panel: 'grow-1 shrink-1 basis-0',
		};
	}

	if (panelWidth === 'one-fourth') {
		return {
			main: 'grow-3 shrink-1 basis-0',
			panel: 'grow-1 shrink-1 basis-0',
		};
	}

	return {
		main: 'grow-1 shrink-1 basis-0',
		panel: `shrink-0 basis-[${panelWidth}px]`,
	};
};

interface ChatLayoutProps {
	className?: string;
	style?: React.CSSProperties;
	panelWidth?: PanelWidthConfig;
	isPanelOpen: boolean;
	rightPanel?: React.ReactNode;
	headerContent?: React.ReactNode;
	onPanelClose?: () => void;
	children: React.ReactNode;
	// Ref to the glass-panel root, so an in-chat overlay (the strategy dock takeover) can
	// measure and cover exactly the chat column as it reflows with the resizable split.
	rootRef?: React.Ref<HTMLDivElement>;
	// Float the header over the content instead of reserving an in-flow band. Only the
	// strategy dock needs this (it hangs from the header band); without the dock the
	// header stays in flow so the chat column is unchanged.
	floatingHeader?: boolean;
}

export const ChatLayout = ({
	className,
	style,
	panelWidth = 'one-fourth',
	isPanelOpen,
	rightPanel,
	headerContent,
	onPanelClose,
	children,
	rootRef,
	floatingHeader = false,
}: ChatLayoutProps) => {
	const flexConfig = getFlexConfig(panelWidth, isPanelOpen);

	return (
		<div
			ref={rootRef}
			className={cn(
				'glass-panel flex h-full min-w-0 flex-col overflow-hidden',
				floatingHeader && 'relative',
				!style && 'w-full',
				className,
			)}
			style={style}
		>
			{/* When floating, the header sits OVER the content so the message list runs
				full-height and scrolls up under these controls (Grok-style) with a top
				fade, rather than hard-clipping at a solid header edge. pointer-events
				pass through the empty bar to the content behind it; only the actual
				controls capture clicks. */}
			<div
				className={cn(
					'flex h-[57px] items-center justify-end gap-3 px-4 py-3',
					floatingHeader
						? `pointer-events-none absolute inset-x-0 top-0 z-20
							[&_a]:pointer-events-auto [&_button]:pointer-events-auto`
						: 'relative',
				)}
			>
				{headerContent}
			</div>
			<div className="flex flex-1 overflow-hidden">
				<div
					className={cn(
						'flex min-w-0 flex-col overflow-hidden',
						flexConfig.main,
					)}
				>
					<div className="flex-1 overflow-hidden">
						<div className="h-full">{children}</div>
					</div>
				</div>

				{isPanelOpen && (
					<div
						className={cn(
							'scrollbar-hidden flex min-w-0 flex-col overflow-y-auto',
							'transition-all duration-300',
							'border-l border-(--glass-border)',
							flexConfig.panel,
						)}
						style={{
							width:
								typeof panelWidth === 'number' ? `${panelWidth}px` : undefined,
						}}
					>
						{onPanelClose && (
							<div className="flex justify-end p-3 pb-0">
								<button
									onClick={onPanelClose}
									className="rounded-full p-1 text-gray-500 transition-colors
										hover:bg-gray-100 hover:text-gray-700"
								>
									<CloseIcon className="size-5" />
								</button>
							</div>
						)}
						{rightPanel}
					</div>
				)}
			</div>
		</div>
	);
};
